import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Sparkles, AlertCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import Header from "../components/Header";
import { checkBillingStatus, fetchUsage } from "../lib/api";
import { trackPurchase } from "../lib/marketingPixels";

const POLL_INTERVAL_MS = 2000;
const MAX_ATTEMPTS = 8;

const BillingSuccess = () => {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const [state, setState] = useState({ status: "checking", message: "" });

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", message: t("billingSuccess.missingSession") });
      return;
    }
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      if (cancelled) return;
      try {
        const res = await checkBillingStatus(sessionId);
        if (cancelled) return;
        if (res.payment_status === "paid" && res.processed) {
          await fetchUsage().catch(() => {});
          const amountUsd =
            typeof res?.amount === "number" && res.amount > 0
              ? res.amount
              : 5.99;
          trackPurchase({
            value: amountUsd,
            currency: (res?.currency || "USD").toUpperCase(),
            content_id: res?.package_id || "pro_monthly",
            content_type: "subscription",
            transaction_id: sessionId,
          });
          setState({
            status: "success",
            message: t("billingSuccess.successMessage"),
          });
          return;
        }
        if (res.status === "expired") {
          setState({ status: "error", message: t("billingSuccess.expired") });
          return;
        }
        attempts += 1;
        if (attempts >= MAX_ATTEMPTS) {
          setState({
            status: "pending",
            message: t("billingSuccess.pendingMessage"),
          });
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        setState({
          status: "error",
          message: e?.response?.data?.detail || t("billingSuccess.verifyError"),
        });
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId, t]);

  return (
    <div className="min-h-screen trubeaute-gradient">
      <Header />
      <div
        className="pt-40 px-6 max-w-xl mx-auto text-center"
        data-testid="billing-success-page"
      >
        {state.status === "checking" && (
          <div data-testid="billing-checking">
            <Loader2 className="h-10 w-10 animate-spin mx-auto text-[#9B6A6C] mb-6" />
            <h1 className="text-3xl font-light text-[#2C2123] mb-3">
              {t("billingSuccess.checkingTitle")}
            </h1>
            <p className="text-[#5C4B51]">
              {t("billingSuccess.checkingBody")}
            </p>
          </div>
        )}

        {state.status === "success" && (
          <div data-testid="billing-success">
            <div className="h-14 w-14 rounded-full bg-[#9B6A6C] mx-auto flex items-center justify-center mb-6">
              <Sparkles className="h-7 w-7 text-white" />
            </div>
            <h1 className="text-4xl font-light text-[#2C2123] mb-3">
              {t("billingSuccess.welcomeBefore")}{" "}
              <span className="font-serif italic text-[#5D4050]">{t("billingSuccess.welcomeProEmphasis")}</span>
            </h1>
            <p className="text-[#5C4B51] mb-8">{state.message}</p>
            <button
              onClick={() => navigate("/onboarding")}
              className="inline-flex items-center justify-center gap-2 bg-[#9B6A6C] text-white hover:bg-[#805557] rounded-full px-8 py-4 transition shadow-[0_6px_20px_rgba(155,106,108,0.35)]"
              data-testid="billing-continue-btn"
            >
              {t("billingSuccess.continueCta")}
            </button>

            <div
              className="mt-14 pt-10 border-t border-[#EADDD7]"
              data-testid="billing-get-app"
            >
              <p className="text-xs uppercase tracking-[0.22em] text-[#9B6A6C] mb-2">
                {t("billingSuccess.appKicker")}
              </p>
              <h2 className="text-2xl font-light text-[#2C2123] mb-3">
                {t("billingSuccess.appHeadline")}
              </h2>
              <p className="text-[#5C4B51] mb-6 max-w-md mx-auto">
                {t("billingSuccess.appBody")}
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href="https://apps.apple.com/app/trubeaute/id0000000000"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-[#2C2123] text-white rounded-xl px-5 py-3 hover:bg-black transition"
                  data-testid="billing-app-store-btn"
                >
                  <svg
                    className="h-6 w-6"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                  </svg>
                  <div className="text-left leading-tight">
                    <div className="text-[9px] uppercase tracking-wider text-white/75">
                      {t("billingSuccess.appStoreCaption")}
                    </div>
                    <div className="text-sm font-medium">{t("billingSuccess.appStoreLabel")}</div>
                  </div>
                </a>
                <a
                  href="https://play.google.com/store/apps/details?id=com.trubeaute.app"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-[#2C2123] text-white rounded-xl px-5 py-3 hover:bg-black transition"
                  data-testid="billing-play-store-btn"
                >
                  <svg
                    className="h-6 w-6"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path d="M3.61 1.814L13.99 12 3.61 22.186c-.38.36-.61.87-.61 1.43V.385c0 .56.23 1.07.61 1.43zm10.92 10.92l2.77-2.77 3.49 2.02c.93.54.93 1.88 0 2.42l-3.49 2.02-2.77-2.77zm-1.06-1.06L3.08.89c.16-.06.33-.1.52-.1.25 0 .49.08.7.2l13.74 7.95-4.57 2.73zm0 2.12l4.57 2.73-13.74 7.95c-.21.12-.45.2-.7.2-.19 0-.36-.04-.52-.1l10.39-10.78z" />
                  </svg>
                  <div className="text-left leading-tight">
                    <div className="text-[9px] uppercase tracking-wider text-white/75">
                      {t("billingSuccess.playStoreCaption")}
                    </div>
                    <div className="text-sm font-medium">{t("billingSuccess.playStoreLabel")}</div>
                  </div>
                </a>
              </div>
              <p className="mt-5 text-xs text-[#8A7D82]">
                {t("billingSuccess.appFooter")}
              </p>
            </div>
          </div>
        )}

        {state.status === "pending" && (
          <div data-testid="billing-pending">
            <div className="h-14 w-14 rounded-full bg-[#F2E8E9] mx-auto flex items-center justify-center mb-6">
              <Loader2 className="h-7 w-7 text-[#9B6A6C]" />
            </div>
            <h1 className="text-3xl font-light text-[#2C2123] mb-3">
              {t("billingSuccess.pendingTitle")}
            </h1>
            <p className="text-[#5C4B51]">{state.message}</p>
          </div>
        )}

        {state.status === "error" && (
          <div data-testid="billing-error">
            <div className="h-14 w-14 rounded-full bg-[#F8EEEE] mx-auto flex items-center justify-center mb-6">
              <AlertCircle className="h-7 w-7 text-[#9B6A6C]" />
            </div>
            <h1 className="text-3xl font-light text-[#2C2123] mb-3">{t("billingSuccess.errorTitle")}</h1>
            <p className="text-[#5C4B51] mb-8">{state.message}</p>
            <button
              onClick={() => navigate("/pricing")}
              className="inline-flex items-center justify-center gap-2 border border-[#D5A8A4] text-[#5D4050] hover:bg-[#F8EEEE] rounded-full px-8 py-4 transition"
              data-testid="billing-back-btn"
            >
              {t("billingSuccess.backCta")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BillingSuccess;
